import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import { Button, Dialog, Field, Input } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useDrainNode } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import type { NodeView } from '@/types/api';
import { errorText } from '@/utils/errors';

/**
 * Moving every site off a server at once (TZ section 10.5), before it is
 * repaired, resized or given back.
 *
 * Each site moves as its own migration — with its own few minutes of
 * maintenance, its own copy and its own way back — so the dialog says so,
 * and asks for the server's name once for all of them.
 */
export function DrainNodeDialog({
  node,
  open,
  onClose,
}: {
  readonly node: NodeView;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [typed, setTyped] = useState('');
  const key = useRef(idempotencyKey('node.drain'));

  const drain = useDrainNode({
    onSuccess: (outcome) => {
      const parts = [`переносов поставлено: ${outcome.queued}`];
      if (outcome.moving > 0) parts.push(`уже переносятся: ${outcome.moving}`);
      if (outcome.skipped > 0) parts.push(`остались на месте: ${outcome.skipped}`);
      toast.success(`Сервер освобождается — ${parts.join(', ')}`);
      close();
    },
  });

  function close(): void {
    if (drain.isPending) return;
    setTyped('');
    drain.reset();
    key.current = idempotencyKey('node.drain');
    onClose();
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (matches) drain.mutate({ id: node.id, confirmation: typed.trim(), key: key.current });
  }

  const matches = typed.trim().toLowerCase() === node.name;

  return (
    <Dialog
      open={open}
      title="Перенести все сайты с сервера"
      description={node.name}
      onClose={close}
      size="sm"
      footer={
        <>
          <Button onClick={close} disabled={drain.isPending}>
            Отмена
          </Button>
          <Button
            variant="danger-solid"
            icon={ArrowRightLeft}
            busy={drain.isPending}
            disabled={!matches}
            type="submit"
            form="drain-node"
          >
            Перенести все
          </Button>
        </>
      }
    >
      <form id="drain-node" className="stack" onSubmit={submit}>
        <ol className="stack stack-tight text-sm">
          <li>Сервер закрывается для новых сайтов.</li>
          <li>Каждый сайт ({node.siteCount}) переносится отдельной задачей.</li>
          <li>Сайт, который не удалось перенести, продолжает работать на этом сервере.</li>
        </ol>
        <Field label="Для подтверждения введите имя сервера" htmlFor="drain-confirm" hint={node.name}>
          <Input
            id="drain-confirm"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={node.name}
            autoComplete="off"
          />
        </Field>
        {drain.isError ? (
          <p className="field-error" role="alert">
            {errorText(drain.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
